import type { components } from '@sportik/api-coach/schema';
import {
  type Query,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/services/api-client';
import { rosterKeyPrefix } from '@/services/roster';

type Client = components['schemas']['Client'];

/** The row a write acts on, with the version the coach last saw. */
type ClientRef = Pick<Client, 'id' | 'version'>;

/** The one definition of the client read: the client page's frame. */
export function clientOptions(clientId: Client['id']) {
  return api.queryOptions('get', '/clients/{clientId}', {
    params: { path: { clientId } },
  });
}

/** One client: the name, the lifecycle state, and the version a write sends back. */
export function useClient(clientId: Client['id']) {
  return useQuery(clientOptions(clientId));
}

/**
 * Every read about one client, on any path and with any parameters: each is
 * generated under the client's own path with the same `clientId`, so the
 * client read's key says what to look for.
 */
function isReadOfClient(clientId: Client['id']) {
  const [method, clientPath, init] = clientOptions(clientId).queryKey;
  return ({ queryKey }: Query) => {
    const [queryMethod, path, queryInit] = queryKey as [
      unknown,
      unknown,
      Partial<typeof init> | undefined,
    ];
    return (
      queryMethod === method &&
      typeof path === 'string' &&
      path.startsWith(clientPath) &&
      queryInit?.params?.path.clientId === init.params.path.clientId
    );
  };
}

// Both are re-read whether the write landed or not: after a conflict what is
// on screen is the stale client. A deleted client has no page to refresh, so
// its reads are only marked: the next visit asks again.
function useClientRefresh({ deleted = false } = {}) {
  const queryClient = useQueryClient();
  return (_data: unknown, _error: unknown, { id }: Pick<Client, 'id'>) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: rosterKeyPrefix() }),
      queryClient.invalidateQueries({
        predicate: isReadOfClient(id),
        refetchType: deleted ? 'none' : 'active',
      }),
    ]);
}

function useClientAction(
  path:
    | '/clients/{clientId}/pause'
    | '/clients/{clientId}/resume'
    | '/clients/{clientId}/stop'
    | '/clients/{clientId}/archive',
) {
  return useMutation({
    mutationFn: ({ id, version }: ClientRef) =>
      api.client.POST(path, {
        params: { path: { clientId: id } },
        body: { version },
      }),
    onSettled: useClientRefresh(),
  });
}

type Rename = ClientRef & {
  /** The coach's own label for the client; `null` clears it. */
  coachDisplayName: string | null;
};

/**
 * Sets or clears the coach's label for a client. The athlete's own name is
 * never written; it shows again once the label is cleared.
 */
export function useRenameClient() {
  return useMutation({
    mutationFn: ({ id, version, coachDisplayName }: Rename) =>
      api.client.PATCH('/clients/{clientId}', {
        params: { path: { clientId: id } },
        body: { coachDisplayName, version },
      }),
    onSettled: useClientRefresh(),
  });
}

/** Suspends coaching; the client stays on the roster under Paused. */
export function usePauseClient() {
  return useClientAction('/clients/{clientId}/pause');
}

/** Takes a paused client back to Active. */
export function useResumeClient() {
  return useClientAction('/clients/{clientId}/resume');
}

/** Ends coaching: access closes and the athlete is free to take another coach. */
export function useStopClient() {
  return useClientAction('/clients/{clientId}/stop');
}

/** Moves a stopped client out of the roster, into Archived. */
export function useArchiveClient() {
  return useClientAction('/clients/{clientId}/archive');
}

/** Takes an archived client out of the roster for good. The contract takes no version here. */
export function useDeleteClient() {
  return useMutation({
    mutationFn: ({ id }: Pick<Client, 'id'>) =>
      api.client.DELETE('/clients/{clientId}', {
        params: { path: { clientId: id } },
      }),
    onSettled: useClientRefresh({ deleted: true }),
  });
}
